import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  Grid,
  Paper
} from '@mui/material';
import { CreditCard } from '@mui/icons-material';

/**
 * Cartões de teste Mercado Pago Brasil
 * 
 * APROVADO:
 * - 5031 4332 1540 6351 (Mastercard)
 * - 5399 7131 5460 3557 (Mastercard)
 * 
 * RECUSADO:
 * - 5031 4332 9811 9117 (insufficient_amount)
 * 
 * CVV: qualquer 3 ou 4 dígitos
 * Data de validade: qualquer data futura (ex: 11/25)
 * Nome: qualquer nome
 * 
 * Documentação: https://www.mercadopago.com.br/developers/pt/docs/checkout-api/testing
 */

// Tipos do Mercado Pago SDK
declare global {
  interface Window {
    MercadoPago: any;
  }
}

interface MercadoPagoCheckoutProps {
  publicKey: string;
  planAmount: number;
  planName: string;
  customerName: string;
  customerEmail: string;
  customerTaxId: string;
  onPaymentSuccess: () => void;
  onPaymentError: (error: string) => void;
}

const MercadoPagoCheckout: React.FC<MercadoPagoCheckoutProps> = ({
  publicKey,
  planAmount,
  planName,
  customerName,
  customerEmail,
  customerTaxId,
  onPaymentSuccess,
  onPaymentError
}) => {
  const [mp, setMp] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [sdkLoading, setSdkLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dados do cartão
  const [cardNumber, setCardNumber] = useState('');
  const [cardholderName, setCardholderName] = useState(customerName);
  const [expirationDate, setExpirationDate] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  const [installments, setInstallments] = useState(1);
  
  // Erros de validação
  const [cardNumberError, setCardNumberError] = useState('');
  const [cardholderNameError, setCardholderNameError] = useState('');
  const [expirationError, setExpirationError] = useState('');
  const [securityCodeError, setSecurityCodeError] = useState('');
  const [identificationError, setIdentificationError] = useState('');

  // Inicializar SDK do Mercado Pago
  useEffect(() => {
    const loadMercadoPagoSDK = () => {
      // Verificar se o script já foi carregado
      if (globalThis.window.MercadoPago) {
        const mercadopago = new globalThis.window.MercadoPago(publicKey);
        setMp(mercadopago);
        setSdkLoading(false);
        return;
      }

      // Carregar script do SDK
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.async = true;
      script.onload = () => {
        const mercadopago = new globalThis.window.MercadoPago(publicKey);
        setMp(mercadopago);
        setSdkLoading(false);
      };
      script.onerror = () => {
        setError('Erro ao carregar SDK do Mercado Pago');
        setSdkLoading(false);
      };
      document.body.appendChild(script);
    };

    loadMercadoPagoSDK();
  }, [publicKey]);

  const formatCardNumber = (value: string) => {
    const numbers = value.replaceAll(/\D/g, '');
    const formatted = numbers.match(/.{1,4}/g)?.join(' ') || numbers;
    return formatted.substring(0, 19); // 16 dígitos + 3 espaços
  };

  const formatExpirationDate = (value: string) => {
    const numbers = value.replaceAll(/\D/g, '');
    if (numbers.length >= 2) {
      return `${numbers.substring(0, 2)}/${numbers.substring(2, 4)}`;
    }
    return numbers;
  };

  const validateCardNumber = (value: string): boolean => {
    const numbers = value.replaceAll(/\D/g, '');
    if (numbers.length < 13 || numbers.length > 19) {
      setCardNumberError('Número de cartão inválido');
      return false;
    }
    setCardNumberError('');
    return true;
  };

  const validateExpirationDate = (value: string): boolean => {
    const [month, year] = value.split('/');
    if (!month || !year || month.length !== 2 || year.length !== 2) {
      setExpirationError('Data inválida (MM/AA)');
      return false;
    }
    
    const monthNum = Number.parseInt(month, 10);
    if (monthNum < 1 || monthNum > 12) {
      setExpirationError('Mês inválido');
      return false;
    }

    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    const yearNum = Number.parseInt(year, 10);

    if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth)) {
      setExpirationError('Cartão vencido');
      return false;
    }

    setExpirationError('');
    return true;
  };

  const validateSecurityCode = (value: string): boolean => {
    if (value.length < 3 || value.length > 4) {
      setSecurityCodeError('CVV inválido');
      return false;
    }
    setSecurityCodeError('');
    return true;
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setCardNumber(formatted);
    if (cardNumberError) setCardNumberError('');
  };

  const handleCardholderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardholderName(e.target.value.toUpperCase());
    if (cardholderNameError) setCardholderNameError('');
  };

  const handleExpirationDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatExpirationDate(e.target.value);
    setExpirationDate(formatted);
    if (expirationError) setExpirationError('');
  };

  const handleSecurityCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replaceAll(/\D/g, '').substring(0, 4);
    setSecurityCode(value);
    if (securityCodeError) setSecurityCodeError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar todos os campos
    const isCardNumberValid = validateCardNumber(cardNumber);
    const isExpirationValid = validateExpirationDate(expirationDate);
    const isSecurityCodeValid = validateSecurityCode(securityCode);
    
    if (!isCardNumberValid || !isExpirationValid || !isSecurityCodeValid) {
      return;
    }

    if (!cardholderName.trim()) {
      setError('Nome do titular é obrigatório');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Extrair mês e ano da data de validade
      const [expirationMonth, expirationYear] = expirationDate.split('/');
      const cardNumberClean = cardNumber.replaceAll(/\D/g, '');
      const taxIdClean = customerTaxId.replaceAll(/\D/g, '');

      // Criar token do cartão usando SDK do Mercado Pago
      const cardData = {
        cardNumber: cardNumberClean,
        cardholderName: cardholderName,
        cardExpirationMonth: expirationMonth,
        cardExpirationYear: `20${expirationYear}`,
        securityCode: securityCode,
        identificationType: taxIdClean.length === 11 ? 'CPF' : 'CNPJ',
        identificationNumber: taxIdClean
      };

      console.log('Criando token do cartão...', { ...cardData, securityCode: '***' });

      const token = await mp.createCardToken(cardData);
      
      if (!token?.id) {
        throw new Error('Erro ao tokenizar cartão');
      }

      console.log('Token criado com sucesso:', token.id);

      // Separar nome e sobrenome
      const nameParts = cardholderName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || nameParts[0];

      // Enviar token para o backend processar o pagamento
      const paymentData = {
        token: token.id,
        transactionAmount: planAmount,
        description: planName,
        installments: installments,
        email: customerEmail,
        firstName: firstName,
        lastName: lastName,
        identificationType: taxIdClean.length === 11 ? 'CPF' : 'CNPJ',
        identificationNumber: taxIdClean,
        planType: 'annual'
      };

      console.log('Processando pagamento...', paymentData);

      // Importar serviço e processar pagamento
      const { processarPagamentoCartao } = await import('../services/mercadopago');
      const response = await processarPagamentoCartao(paymentData);

      console.log('Resposta do pagamento:', response);
      console.log('Success:', response.success);
      console.log('Error Message:', response.errorMessage);

      if (response.success) {
        console.log('Pagamento aprovado!');
        onPaymentSuccess();
      } else {
        // Backend retornou success=false, processar array de erros
        console.error('Backend retornou erro:', response);
        
        let errorDetails = '';
        let hasFieldError = false;
        
        // Processar array de erros se existir
        if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
          errorDetails = '\nDetalhes dos erros:\n';
          
          for (const error of response.errors) {
            const code = error.code || 'desconhecido';
            const message = error.message || 'Valor inválido';
            
            errorDetails += `- ${message} (código: ${code})\n`;
            
            // Mapear erro para campo específico
            switch (code) {
              case '316': // cardholderName
                setCardholderNameError(message);
                hasFieldError = true;
                break;
              case '324': // identificationNumber
                setIdentificationError(message);
                hasFieldError = true;
                break;
              case '205': // cardNumber
              case 'E301': // cardNumber
                setCardNumberError(message);
                hasFieldError = true;
                break;
              case '208': // expirationMonth/Year
              case '325': // expirationMonth
              case '326': // expirationYear
                setExpirationError(message);
                hasFieldError = true;
                break;
              case '224': // securityCode
              case 'E302': // securityCode
                setSecurityCodeError(message);
                hasFieldError = true;
                break;
            }
          }
        }
        
        // Montar mensagem de erro
        const backendError = response.errorMessage || 'Erro ao processar pagamento';
        let finalErrorMsg = '';
        
        if (hasFieldError) {
          finalErrorMsg = `Erro de validação. Verifique os campos destacados.${errorDetails}`;
        } else {
          finalErrorMsg = `${backendError}${errorDetails}`;
        }
        
        setError(finalErrorMsg);
        onPaymentError(backendError);
        // Não fazer return aqui - deixar o finally executar
      }

    } catch (err: any) {
      console.error('Erro ao processar pagamento:', err);
      console.error('Detalhes do erro:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      
      // Limpar erros anteriores
      setCardNumberError('');
      setCardholderNameError('');
      setExpirationError('');
      setSecurityCodeError('');
      setIdentificationError('');
      setError(null);
      
      // Extrair informações de erro do backend
      const responseData = err.response?.data;
      const httpStatus = err.response?.status;
      let errorDetails = '';
      let hasFieldError = false;
      
      // Montar mensagem de erro detalhada
      if (httpStatus) {
        errorDetails += `Código HTTP: ${httpStatus}\n`;
      }
      
      // Verificar se há errorMessage do backend
      if (responseData?.errorMessage) {
        errorDetails += `${responseData.errorMessage}\n`;
      }
      
      // Verificar se a resposta é um array de erros (formato do Mercado Pago)
      let causes = null;
      if (Array.isArray(responseData)) {
        causes = responseData;
      } else if (responseData?.cause && Array.isArray(responseData.cause)) {
        causes = responseData.cause;
      }
      
      // Processar array de erros se existir
      if (causes && causes.length > 0) {
        errorDetails += '\nDetalhes dos erros:\n';
        
        for (const cause of causes) {
          const code = cause.code || 'desconhecido';
          const message = cause.message || 'Valor inválido';
          
          errorDetails += `- ${message} (código: ${code})\n`;
          
          // Mapear erro para campo específico
          switch (code) {
            case '316': // cardholderName
              setCardholderNameError(message);
              hasFieldError = true;
              break;
            case '324': // identificationNumber
              setIdentificationError(message);
              hasFieldError = true;
              break;
            case '205': // cardNumber
            case 'E301': // cardNumber
              setCardNumberError(message);
              hasFieldError = true;
              break;
            case '208': // expirationMonth/Year
            case '325': // expirationMonth
            case '326': // expirationYear
              setExpirationError(message);
              hasFieldError = true;
              break;
            case '224': // securityCode
            case 'E302': // securityCode
              setSecurityCodeError(message);
              hasFieldError = true;
              break;
          }
        }
      }
      
      // Montar mensagem de erro final
      let finalErrorMsg = '';
      if (hasFieldError) {
        finalErrorMsg = `Erro de validação. Verifique os campos destacados.\n\n${errorDetails}`;
      } else {
        const errorMessage = responseData?.message || responseData?.errorMessage || err.message || 'Erro ao processar pagamento';
        finalErrorMsg = `${errorMessage}\n\n${errorDetails}`;
      }
      
      setError(finalErrorMsg);
      
      // Chamar callback de erro
      const finalErrorMessage = responseData?.errorMessage || responseData?.message || err.message || 'Erro ao processar pagamento';
      onPaymentError(`${finalErrorMessage}\n\nCódigo HTTP: ${httpStatus || 'desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };

  if (sdkLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Carregando formulário de pagamento...
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <CreditCard sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Dados do Cartão de Crédito
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pagamento seguro processado pelo Mercado Pago
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-wrap' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={{xs:12}}>
            <TextField
              fullWidth
              label="Número do Cartão"
              value={cardNumber}
              onChange={handleCardNumberChange}
              onBlur={() => validateCardNumber(cardNumber)}
              error={!!cardNumberError}
              helperText={cardNumberError || 'Digite o número do seu cartão. Para testes use: 5031 4332 1540 6351 (aprovado) ou 5031 4332 9811 9117 (recusado)'}
              placeholder="1234 5678 9012 3456"
              required
              disabled={loading}
            />
          </Grid>

          <Grid size={{xs:12}}>
            <TextField
              fullWidth
              label="Nome do Titular"
              value={cardholderName}
              onChange={handleCardholderNameChange}
              error={!!cardholderNameError}
              helperText={cardholderNameError || "Exatamente como está impresso no cartão"}
              placeholder="NOME COMO ESTÁ NO CARTÃO"
              required
              disabled={loading}
            />
          </Grid>

          <Grid size={{xs: 6}}>
            <TextField
              fullWidth
              label="Validade"
              value={expirationDate}
              onChange={handleExpirationDateChange}
              onBlur={() => validateExpirationDate(expirationDate)}
              error={!!expirationError}
              helperText={expirationError || 'MM/AA'}
              placeholder="MM/AA"
              required
              disabled={loading}
            />
          </Grid>

          <Grid size={{xs: 6}}>
            <TextField
              fullWidth
              label="CVV"
              value={securityCode}
              onChange={handleSecurityCodeChange}
              onBlur={() => validateSecurityCode(securityCode)}
              error={!!securityCodeError}
              helperText={securityCodeError || 'Código de segurança'}
              placeholder="123"
              type="password"
              required
              disabled={loading}
            />
          </Grid>

          <Grid size={{xs:12}}>
            {identificationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>CPF/CNPJ:</strong> {identificationError}
              </Alert>
            )}
          </Grid>

          <Grid size={{xs:12}}>
            <TextField
              fullWidth
              select
              label="Parcelas"
              value={installments}
              onChange={(e) => setInstallments(Number.parseInt(e.target.value, 10))}
              disabled={loading}
              helperText="Escolha o número de parcelas"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => {
                const installmentAmount = planAmount / num;
                return (
                  <MenuItem key={num} value={num}>
                    {num}x de R$ {installmentAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {num === 1 ? ' (à vista)' : ''}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid size={{xs:12}}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CreditCard />}
              sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
            >
              {loading ? 'Processando...' : `Pagar R$ ${planAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </Button>
          </Grid>
        </Grid>
      </form>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          🔒 Seus dados estão protegidos com criptografia de ponta a ponta
        </Typography>
      </Box>
    </Paper>
  );
};

export default MercadoPagoCheckout;
